import React, { useState, useEffect } from 'react';
import { getAdmins, createAdmin, updateAdmin, deleteAdmin } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { UserPlus, Loader2, Trash2, Pencil, Shield, Eye, EyeOff } from 'lucide-react';

export const AdminManagementPage = () => {
    const { user: currentUser } = useAuth();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: ''
    });

    useEffect(() => {
        loadAdmins();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAdmins = async () => {
        try {
            const res = await getAdmins();
            setAdmins(res.data);
        } catch (err) {
            toast.error('Fehler beim Laden der Admins');
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setFormData({ name: '', email: '', password: '' });
        setIsEditing(false);
        setSelectedAdmin(null);
        setDialogOpen(true);
    };

    const openEditDialog = (admin) => {
        setFormData({ 
            name: admin.name, 
            email: admin.email, 
            password: '' 
        });
        setIsEditing(true);
        setSelectedAdmin(admin);
        setShowPassword(false);
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        
        try {
            if (isEditing) {
                // Only send fields that have values
                const updateData = {};
                if (formData.name && formData.name !== selectedAdmin.name) {
                    updateData.name = formData.name;
                }
                if (formData.email && formData.email !== selectedAdmin.email) {
                    updateData.email = formData.email;
                }
                if (formData.password) {
                    updateData.password = formData.password;
                }
                
                if (Object.keys(updateData).length === 0) {
                    toast.info('Keine Änderungen vorgenommen');
                    setDialogOpen(false);
                    return;
                }
                
                await updateAdmin(selectedAdmin.id, updateData);
                toast.success('Admin aktualisiert');
            } else {
                if (!formData.password) {
                    toast.error('Passwort ist erforderlich');
                    setSaving(false);
                    return;
                }
                await createAdmin(formData);
                toast.success('Admin erstellt');
            }
            setDialogOpen(false);
            loadAdmins();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = (admin) => {
        setSelectedAdmin(admin);
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        try {
            await deleteAdmin(selectedAdmin.id);
            toast.success('Admin gelöscht');
            setDeleteDialogOpen(false);
            loadAdmins();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-6" data-testid="admin-management-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold">Admin-Verwaltung</h1>
                    <p className="text-muted-foreground">{admins.length} Administrator{admins.length !== 1 ? 'en' : ''}</p>
                </div>
                <Button onClick={openCreateDialog} data-testid="create-admin-btn">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Neuen Admin erstellen
                </Button>
            </div>

            {/* Admin List */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Administratoren
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        {admins.map(admin => (
                            <div 
                                key={admin.id}
                                className={`p-4 border rounded-sm flex items-center justify-between ${
                                    admin.id === currentUser?.id ? 'bg-primary/5 border-primary/20' : ''
                                }`}
                                data-testid={`admin-${admin.id}`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-lg">
                                        {admin.name.charAt(0)}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="font-semibold">{admin.name}</p>
                                            {admin.id === currentUser?.id && (
                                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                                    Sie
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">{admin.email}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {admin.password_plain && (
                                        <span className="text-sm text-muted-foreground font-mono bg-muted px-2 py-1 rounded hidden md:block">
                                            PW: {admin.password_plain}
                                        </span>
                                    )}
                                    <span className="text-sm text-muted-foreground mr-4 hidden md:block">
                                        Seit {formatDate(admin.created_at)}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => openEditDialog(admin)}
                                        data-testid={`edit-admin-${admin.id}`}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    {admin.id !== currentUser?.id && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => confirmDelete(admin)}
                                            data-testid={`delete-admin-${admin.id}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="admin-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {isEditing ? 'Admin bearbeiten' : 'Neuen Admin erstellen'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Name</Label>
                            <Input
                                placeholder="Max Mustermann"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                required={!isEditing}
                                data-testid="admin-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>E-Mail</Label>
                            <Input
                                type="email"
                                placeholder="admin@beispiel.de"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                required={!isEditing}
                                data-testid="admin-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>
                                Passwort
                                {isEditing && <span className="text-muted-foreground text-xs ml-2">(leer lassen um beizubehalten)</span>}
                            </Label>
                            {/* Show stored password for editing */}
                            {isEditing && selectedAdmin?.password_plain && (
                                <div className="p-2 bg-muted rounded-md mb-2">
                                    <p className="text-xs text-muted-foreground">Aktuelles Passwort:</p>
                                    <p className="font-mono text-sm">{selectedAdmin.password_plain}</p>
                                </div>
                            )}
                            <div className="relative">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder={isEditing ? 'Neues Passwort eingeben...' : 'Mindestens 6 Zeichen'}
                                    value={formData.password}
                                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                                    required={!isEditing}
                                    minLength={!isEditing || formData.password ? 6 : undefined}
                                    data-testid="admin-password-input"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={saving}>
                                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                {isEditing ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Admin löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie den Admin "{selectedAdmin?.name}" wirklich löschen? 
                            Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AdminManagementPage;
